import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, ArrowRight, ShieldCheck } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Star, Sparkle } from "@/components/Decor";
import { TerryCorner } from "@/components/Terry";
import GoogleSignIn from "@/components/GoogleSignIn";
import { identifier as vIdentifier, password as vPassword, maxLen, firstError } from "@/lib/validate";

export default function Auth() {
    const nav = useNavigate();
    const [sp, setSp] = useSearchParams();
    // Derived, not copied into state: the navbar's "Sign in" link only changes the
    // URL, and this component stays mounted, so a useState initialiser would keep
    // showing whichever mode the page first opened in.
    const mode = sp.get("mode") === "signup" ? "signup" : "login";
    const setMode = (m) => setSp(m === "signup" ? { mode: "signup" } : {}, { replace: true });
    const [stage, setStage] = useState("form"); // form -> otp
    const [identifier, setId] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [newPw, setNewPw] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [devOtp, setDevOtp] = useState(null);
    const [sentTo, setSentTo] = useState(null);
    const [busy, setBusy] = useState(false);
    const [resent, setResent] = useState(0);   // seconds until resend is allowed again
    const { signup, verify, login, googleSignIn, resendOtp,
            forgotPassword, resetPassword } = useAuth();

    // Switching between sign-in and sign-up mid-OTP should return to the form
    // rather than stranding them on a code screen for the other flow.
    React.useEffect(() => { setStage("form"); }, [mode]);

    // Cooldown so the resend button can't be hammered into the server's 3/10min limit.
    React.useEffect(() => {
        if (resent <= 0) return;
        const t = setTimeout(() => setResent((v) => v - 1), 1000);
        return () => clearTimeout(t);
    }, [resent]);

    // Codes only go out by email, so a phone signup has to name one too —
    // otherwise the account has no way back from a forgotten password.
    const looksLikePhone = identifier.trim().length > 0 && !identifier.includes("@");
    const needsEmail = mode === "signup" && looksLikePhone;

    const submit = async (e) => {
        e.preventDefault();
        // Signing in only needs the identifier to be well-formed; an old account's
        // password has to stay usable whatever today's length rule says.
        const bad = firstError([
            vIdentifier(identifier),
            mode === "signup" ? vPassword(password) : null,
            mode === "signup" ? maxLen(name, 120, "Name") : null,
            needsEmail && !email.includes("@") ? "Please add an email address — that's where your code goes" : null,
        ]);
        if (bad) { toast.error(bad); return; }
        setBusy(true);
        try {
            if (mode === "signup") {
                const r = await signup(identifier, password, name, needsEmail ? email : undefined);
                setDevOtp(r.dev_otp);
                setSentTo(r.sent_to || null);
                setStage("otp");
                if (r.delivered === false && !r.dev_otp) {
                    toast.error("We couldn't email your code. Please try again in a moment.");
                } else {
                    toast.success(`We sent a code to ${r.sent_to || identifier}.`);
                }
            } else {
                await login(identifier, password);
                toast.success("Welcome back.");
                nav("/library");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong");
        } finally { setBusy(false); }
    };

    const onGoogle = async (credential) => {
        setBusy(true);
        try {
            const { user } = await googleSignIn(credential);
            toast.success(`Welcome, ${user.name || "friend"}.`);
            nav("/library");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Google sign-in failed");
        } finally { setBusy(false); }
    };

    const doResend = async () => {
        const bad = vIdentifier(identifier);
        if (bad) { toast.error(bad); return; }
        setBusy(true);
        try {
            const r = await resendOtp(identifier);
            setDevOtp(r.dev_otp);
            setResent(30);
            toast.success("New code sent.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not resend the code");
        } finally { setBusy(false); }
    };

    // --- password recovery ---
    const startForgot = async () => {
        const bad = vIdentifier(identifier);
        if (bad) { toast.error(bad); return; }
        setBusy(true);
        try {
            const r = await forgotPassword(identifier);
            setDevOtp(r.dev_otp);
            setSentTo(r.sent_to || null);
            setOtp(""); setNewPw(""); setConfirmPw("");
            setStage("reset");
            // Deliberately unconditional: saying "no such account" here would
            // turn this box into a way to test which emails are registered.
            toast.success(r.sent_to
                ? `If that account exists, a reset code is on its way to ${r.sent_to}.`
                : "If that account exists, a reset code is on its way.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send a reset code");
        } finally { setBusy(false); }
    };

    const doReset = async () => {
        const bad = firstError([
            /^\d{6}$/.test(otp) ? null : "Enter the 6-digit code",
            vPassword(newPw),
            newPw === confirmPw ? null : "Those passwords don't match",
        ]);
        if (bad) { toast.error(bad); return; }
        setBusy(true);
        try {
            await resetPassword(identifier, otp, newPw);
            // Every session was invalidated server-side, so they sign in fresh.
            setPassword(""); setNewPw(""); setConfirmPw(""); setOtp("");
            setStage("form");
            toast.success("Password changed. Sign in with your new one.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not reset your password");
        } finally { setBusy(false); }
    };

    const doVerify = async () => {
        if (!/^\d{6}$/.test(otp)) { toast.error("Enter the 6-digit code"); return; }
        setBusy(true);
        try {
            await verify(identifier, otp);
            toast.success("You're in. Let's begin your story.");
            nav("/create");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invalid code");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen magic-bg">
            <Toaster position="top-center" />
            <Navbar />
            <TerryCorner pose="wave" />
            <div className="max-w-md mx-auto px-6 py-16 relative">
                <div className="absolute -top-4 left-6 animate-float">
                    <Star size={22} />
                </div>
                <div className="absolute top-10 right-4 animate-twinkle">
                    <Sparkle size={20} />
                </div>
                <AnimatePresence mode="wait">
                    {stage === "form" && (
                        <motion.div key="form" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <div className="inline-flex items-center gap-2 rounded-full bg-mint/60 border-2 border-plum/5 px-3 py-1 text-xs text-plum shadow-sm mb-3">
                                {mode === "signup" ? "Create an account" : "Welcome back"}
                            </div>
                            <h1 className="font-display text-5xl tracking-tight text-plum mb-2">
                                {mode === "signup" ? "Begin your story" : "Continue your story"}
                            </h1>
                            <div className="script-note text-2xl mb-4">one code, no password to remember</div>
                            <p className="text-sm text-plum/60 mb-8">
                                Sign {mode === "signup" ? "up" : "in"} with your email or phone. We'll send a one-time code to verify it's you.
                            </p>
                            <form onSubmit={submit} className="card-cloud p-7 space-y-4">
                                {mode === "signup" && (
                                    <div>
                                        <Label className="text-xs uppercase tracking-[0.15em] text-plum/60">Your name</Label>
                                        <Input value={name} onChange={(e) => setName(e.target.value)}
                                            placeholder="Ananya" maxLength={120}
                                            className="mt-1.5 bg-white rounded-full h-11 px-4" data-testid="auth-name-input" />
                                    </div>
                                )}
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.15em] text-plum/60">Email or phone</Label>
                                    <div className="relative mt-1.5">
                                        <Input value={identifier} onChange={(e) => setId(e.target.value)}
                                            placeholder="you@example.com or +91…" required maxLength={254}
                                            className="bg-white pl-10 rounded-full h-11" data-testid="auth-identifier-input" />
                                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-plum/40">
                                            {identifier.includes("@") ? <Mail className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
                                        </div>
                                    </div>
                                </div>
                                {/* Appears the moment the identifier stops looking like an email:
                                    codes go out by email only, and an account with no email
                                    address has no way back from a forgotten password. */}
                                {needsEmail && (
                                    <div data-testid="auth-email-field">
                                        <Label className="text-xs uppercase tracking-[0.15em] text-plum/60">Email address</Label>
                                        <div className="relative mt-1.5">
                                            <Input value={email} onChange={(e) => setEmail(e.target.value)}
                                                type="email" placeholder="you@example.com" required maxLength={254}
                                                className="bg-white pl-10 rounded-full h-11" data-testid="auth-email-input" />
                                            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-plum/40">
                                                <Mail className="w-4 h-4" />
                                            </div>
                                        </div>
                                        <div className="script-note text-lg mt-1">we send your code here, not by SMS</div>
                                    </div>
                                )}
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.15em] text-plum/60">Password</Label>
                                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                                        placeholder="At least 8 characters" required minLength={8} maxLength={128}
                                        className="mt-1.5 bg-white rounded-full h-11 px-4" data-testid="auth-password-input" />
                                </div>
                                {mode === "login" && (
                                    <div className="text-right -mt-1">
                                        <button type="button" onClick={startForgot} disabled={busy}
                                            className="text-xs text-bubblegum font-semibold disabled:opacity-50"
                                            data-testid="auth-forgot-btn">
                                            Forgot your password?
                                        </button>
                                    </div>
                                )}
                                <button type="submit" disabled={busy}
                                    className="w-full btn-sticker mt-2 disabled:opacity-60"
                                    data-testid="auth-submit-btn">
                                    {busy ? "Please wait…" : (mode === "signup" ? "✨ Send verification code" : "Sign in")}
                                </button>
                            </form>

                            <GoogleSignIn onCredential={onGoogle} disabled={busy}
                                text={mode === "signup" ? "signup_with" : "signin_with"} />

                            <div className="mt-6 text-center text-sm text-plum/60">
                                {mode === "signup" ? (
                                    <>Already have an account? <button onClick={() => setMode("login")} className="text-bubblegum font-semibold" data-testid="auth-switch-login">Sign in</button></>
                                ) : (
                                    <>New to Tales with Terry? <button onClick={() => setMode("signup")} className="text-bubblegum font-semibold" data-testid="auth-switch-signup">Create an account</button></>
                                )}
                            </div>
                        </motion.div>
                    )}
                    {stage === "otp" && (
                        <motion.div key="otp" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <div className="inline-flex items-center gap-2 rounded-full bg-buttercup/70 border-2 border-plum/5 px-3 py-1 text-xs text-plum shadow-sm mb-3">Verify it's you</div>
                            <h1 className="font-display text-5xl tracking-tight text-plum mb-2">Enter your code ✨</h1>
                            <p className="text-sm text-plum/60 mb-8">We sent a 6-digit code to <b className="text-plum">{sentTo || identifier}</b>. It expires in 10 minutes.</p>
                            <div className="card-cloud p-7 space-y-6">
                                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="auth-otp-input">
                                    <InputOTPGroup>
                                        {[0,1,2,3,4,5].map(i => (
                                            <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg" />
                                        ))}
                                    </InputOTPGroup>
                                </InputOTP>
                                {devOtp && (
                                    <div className="flex items-center gap-2 text-xs text-plum bg-mint/50 rounded-full px-4 py-2">
                                        <ShieldCheck className="w-4 h-4" /> Dev mode OTP: <span className="font-mono font-bold">{devOtp}</span>
                                    </div>
                                )}
                                <button onClick={doVerify} disabled={busy || otp.length < 6}
                                    className="w-full btn-sticker disabled:opacity-60"
                                    data-testid="auth-verify-btn">
                                    {busy ? "Verifying…" : "✨ Verify & continue"}
                                </button>
                                <div className="text-center text-sm text-plum/60">
                                    Didn't get it?{" "}
                                    <button onClick={doResend} disabled={busy || resent > 0}
                                        className="text-bubblegum font-semibold disabled:opacity-50"
                                        data-testid="auth-resend-btn">
                                        {resent > 0 ? `Resend in ${resent}s` : "Send a new code"}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                    {stage === "reset" && (
                        <motion.div key="reset" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <div className="inline-flex items-center gap-2 rounded-full bg-buttercup/70 border-2 border-plum/5 px-3 py-1 text-xs text-plum shadow-sm mb-3">Choose a new password</div>
                            <h1 className="font-display text-5xl tracking-tight text-plum mb-2">Let's get you back in</h1>
                            <p className="text-sm text-plum/60 mb-8">
                                We sent a 6-digit code to <b className="text-plum">{sentTo || identifier}</b>. It expires in 10 minutes.
                            </p>
                            <div className="card-cloud p-7 space-y-5">
                                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="reset-otp-input">
                                    <InputOTPGroup>
                                        {[0,1,2,3,4,5].map(i => (
                                            <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg" />
                                        ))}
                                    </InputOTPGroup>
                                </InputOTP>
                                {devOtp && (
                                    <div className="flex items-center gap-2 text-xs text-plum bg-mint/50 rounded-full px-4 py-2">
                                        <ShieldCheck className="w-4 h-4" /> Dev mode OTP: <span className="font-mono font-bold">{devOtp}</span>
                                    </div>
                                )}
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.15em] text-plum/60">New password</Label>
                                    <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                                        placeholder="At least 8 characters" minLength={8} maxLength={128}
                                        className="mt-1.5 bg-white rounded-full h-11 px-4" data-testid="reset-password-input" />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-[0.15em] text-plum/60">Confirm new password</Label>
                                    <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                                        placeholder="Type it once more" minLength={8} maxLength={128}
                                        className="mt-1.5 bg-white rounded-full h-11 px-4" data-testid="reset-confirm-input" />
                                </div>
                                <button onClick={doReset} disabled={busy}
                                    className="w-full btn-sticker disabled:opacity-60" data-testid="reset-submit-btn">
                                    {busy ? "Saving…" : "✨ Set my new password"}
                                </button>
                                <div className="text-center text-sm text-plum/60">
                                    Didn't get a code?{" "}
                                    <button onClick={startForgot} disabled={busy}
                                        className="text-bubblegum font-semibold disabled:opacity-50"
                                        data-testid="reset-resend-btn">Send another</button>
                                </div>
                                <div className="text-center text-sm text-plum/60">
                                    <button onClick={() => setStage("form")}
                                        className="text-plum/50 hover:text-plum" data-testid="reset-back-btn">
                                        ← Back to sign in
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}
